import type { DragConstraint, DragUpdateResult } from "@project/emerge-types";
import type * as THREE from "three";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import type { RackInteriorOptions } from "../../interfaces/RackInteriorOptions";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

const MIN_SIZE = 50;
const MAX_SIZE = 5000;

/**
 * Drag handler for RACK interior type.
 * Always enabled - resizes overall dimensions with proportional scaling.
 */
export const RackCornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings): boolean {
    return settings.interiorOptions.type === InteriorType.RACK;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const isBottom = caseCornerPosition.endsWith("Bottom");
    const isLeft = caseCornerPosition.startsWith("left");
    const isFront = caseCornerPosition.includes("Front");

    const rack = settings.interiorOptions as RackInteriorOptions;
    const currentWidth =
      typeof rack.width === "number" ? rack.width : innerDimensions.x;
    const currentHeight = rack.heights?.reduce((sum, h) => sum + h, 0) ?? 0;
    const currentDepth = rack.depth ?? innerDimensions.z;

    const xSign = isLeft ? -1 : 1;
    const zSign = isFront ? -1 : 1;

    if (isBottom) {
      // Bottom corners: resize x and z on horizontal plane
      const halfMinX = MIN_SIZE / 2;
      const halfMaxX = MAX_SIZE / 2;
      const halfMinZ = MIN_SIZE / 2;
      const halfMaxZ = MAX_SIZE / 2;

      const minX = xSign * halfMinX;
      const maxX = xSign * halfMaxX;
      const minZ = zSign * halfMinZ;
      const maxZ = zSign * halfMaxZ;

      const dragConstraint: DragConstraint = {
        type: "surface",
        planeNormal: [0, 1, 0],
        bounds: {
          min: [Math.min(minX, maxX), position.y, Math.min(minZ, maxZ)],
          max: [Math.max(minX, maxX), position.y, Math.max(minZ, maxZ)],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_xz",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "xz",
          interiorType: "rack",
          xSign,
          zSign,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    } else {
      // Top corners: resize height along vertical axis
      const bottomY = -innerDimensions.y / 2;
      const minY = bottomY + MIN_SIZE;
      const maxY = bottomY + MAX_SIZE;

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 1, 0],
        bounds: {
          min: [position.x, minY, position.z],
          max: [position.x, maxY, position.z],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_y",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "y",
          interiorType: "rack",
          bottomY,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    }
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "xz" | "y";
    const interior = currentSettings.interiorOptions as RackInteriorOptions;

    if (mode === "xz") {
      const newWidth = Math.max(50, Math.round(Math.abs(newPosition.x) * 2));
      const newDepth = Math.max(50, Math.round(Math.abs(newPosition.z) * 2));

      // Use current dimensions to calculate scale (prevents accumulation)
      const actualCurrentWidth =
        typeof interior.width === "number" ? interior.width : 465;
      const widthScale =
        actualCurrentWidth > 0 ? newWidth / actualCurrentWidth : 1;

      // Scale widths proportionally, keep heights unchanged
      const scaledWidths = interior.widths.map((row) =>
        row.map((w) => Math.max(50, Math.round(w * widthScale))),
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            width: newWidth,
            depth: newDepth,
            widths: scaledWidths,
          },
        },
        displayValues: {
          primary: {
            label: "W×D",
            value: newWidth,
            unit: `×${newDepth}mm`,
          },
        },
      };
    } else {
      const bottomY = settingsContext.bottomY as number;
      const newHeight = Math.max(50, Math.round(newPosition.y - bottomY));

      // Use current dimensions to calculate scale (prevents accumulation)
      const actualCurrentHeight = interior.heights.reduce(
        (sum, h) => sum + h,
        0,
      );
      const heightScale =
        actualCurrentHeight > 0 ? newHeight / actualCurrentHeight : 1;

      // Scale heights proportionally, keep widths unchanged
      const scaledHeights = interior.heights.map((h) =>
        Math.max(50, Math.round(h * heightScale)),
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            heights: scaledHeights,
          },
        },
        displayValues: {
          primary: {
            label: "H",
            value: newHeight,
            unit: "mm",
          },
        },
      };
    }
  },
};
